import { cloneDeep, forEach, isEqual } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import {
  AppPlatformDataAttributeCurrencyCondition,
  AttributeType,
  conditionFromDto,
  ConditionType,
} from 'src/actions/rules/condition';
import { Button } from 'src/presentation/components/agent_desktop_components';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import Input, { InputType } from 'src/presentation/components/forms/Input';
import RootRuleErrors from './shared/RootRuleErrors';
import { Row, Section } from 'src/presentation/components/forms/Layout';
import RuleActions from './RuleActions';
import rulesResource, { Rule } from 'src/actions/rules/rulesResource';
import RuleConditions from './RuleConditions';
import RuleTrigger from './RuleTrigger';
import { StyledBody } from './shared/StyledComponents';
import Switch from 'src/presentation/components/forms/Switch';
import { TriggerType } from 'src/actions/rules/trigger';
import { useOnSave } from './shared/editorHelpers';
import validateRule from 'src/actions/rules/validateRule';

const dataAidPrefix = 'admin-ruleEditor';

const RuleEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const add = rulesResource.effects.add;
  const resetErrors = rulesResource.actions.resetErrors as any;
  const update = rulesResource.effects.update;
  const validate = rulesResource.effects.validate;
  const dispatch = useDispatch();

  const formItem = cloneDeep(item);
  forEach(formItem?.conditions, condition => {
    if (condition.type === ConditionType.AppPlatformDataAttribute && condition.attrType === AttributeType.Currency) {
      const translatedCondition = conditionFromDto(condition) as AppPlatformDataAttributeCurrencyCondition;
      condition.value = translatedCondition.value;
      condition.begin = translatedCondition.begin;
      condition.end = translatedCondition.end;
    }
  });

  const formContext = useForm({
    add,
    defaultItem: {
      name: '',
      disabled: false,
      conditions: [],
      actions: {},
      trigger: { type: TriggerType.IncomingCommunication }, // to be removed
      _version: 1,
    },
    errors,
    item: formItem,
    resetErrors,
    root,
    update,
    validate,
  });

  const { isNew, onClose, overrideState, workingState } = formContext;
  const rule = useMemo(() => {
    return new Rule(workingState);
  }, [workingState]);

  const [isConfirmCancelModalVisible, setConfirmCancelModalVisible] = useState(false);
  const [isComplete, setComplete] = useState(false);

  const isModified = useMemo(() => {
    return !isEqual(formItem, workingState);
  }, [formItem, workingState]);

  const handleSave = useOnSave({
    ...formContext,
    add,
    resetErrors,
    setComplete,
    update,
    validateRule,
    workingState: rule,
  });

  const handleStay = useCallback(() => {
    setConfirmCancelModalVisible(false);
  }, [setConfirmCancelModalVisible]);

  const confirmCancel = useCallback(() => {
    isModified ? setConfirmCancelModalVisible(true) : onClose();
  }, [isModified, onClose, setConfirmCancelModalVisible]);

  const updateRule = useCallback(
    rule => {
      overrideState(rule.toJs());
      errors.length && dispatch(resetErrors());
    },
    [dispatch, errors.length, overrideState, resetErrors]
  );

  const onSetTrigger = useCallback(
    type => {
      rule.setTrigger(type);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onUpdateTrigger = useCallback(
    trigger => {
      rule.updateTrigger(trigger);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onAddCondition = useCallback(
    (conditionType, attrName, attrType) => {
      rule.addCondition(conditionType, attrName, attrType);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onUpdateCondition = useCallback(
    ({ condition, index }) => {
      rule.updateCondition(condition, index);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onDeleteCondition = useCallback(
    index => {
      rule.deleteCondition(index);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onAddAction = useCallback(
    action => {
      rule.addAction(action);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onDeleteAction = useCallback(
    key => {
      rule.deleteAction(key);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onUpdateAction = useCallback(
    (key, value) => {
      rule.updateActionValue(key, value);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onChangeName = useCallback(
    e => {
      rule.setName(e.target.value);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onChangeOrder = useCallback(
    e => {
      const order = parseInt(e.target.value);
      rule.setOrder(isNaN(order) ? 0 : order);
      updateRule(rule);
    },
    [rule, updateRule]
  );

  const onToggleDisabled = useCallback(() => {
    rule.toggleDisabled();
    updateRule(rule);
  }, [rule, updateRule]);

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <H1>{isNew ? 'Add' : 'Edit'} Rule</H1>
        <StyledBody data-aid={dataAidPrefix}>
          <Description>
            Check out our{' '}
            <ExternalLink
              data-aid="learn-about-rules-library"
              label="sample rules library"
              to="https://help.gladly.com/docs/sample-rules-library"
            />{' '}
            for common rules and how to implement them.
          </Description>
          <Section>
            <Row>
              <Input
                data-aid={`${dataAidPrefix}-nameInput`}
                attr="name"
                autoFocus
                label="Name"
                onChange={onChangeName}
                placeholder="Type Rule name"
              />
              {!isNew ? (
                <SortOrderInput
                  data-aid={`${dataAidPrefix}-orderInput`}
                  attr="order"
                  label="Sort order"
                  onChange={onChangeOrder}
                  type={InputType.number}
                />
              ) : null}
              <Switch
                data-aid={`${dataAidPrefix}-activeSwitch`}
                attr="disabled"
                label="Active"
                onChange={onToggleDisabled}
                flipValue
              />
            </Row>
          </Section>
          <RuleTrigger trigger={rule.trigger} onSetTrigger={onSetTrigger} onUpdateTrigger={onUpdateTrigger} />
          <RuleConditions
            conditions={rule.conditions}
            title={'Conditions'}
            errors={errors}
            triggerType={rule.trigger.type}
            onAddCondition={onAddCondition}
            onDeleteCondition={onDeleteCondition}
            onUpdateCondition={onUpdateCondition}
          />
          <RuleActions
            actions={rule.actions}
            title={'Actions'}
            errors={errors}
            triggerType={rule.trigger.type}
            onAddAction={onAddAction}
            onDeleteAction={onDeleteAction}
            onUpdateAction={onUpdateAction}
          />
          <RootRuleErrors errors={errors} />
        </StyledBody>
        <Footer>
          <Button data-aid={`${dataAidPrefix}-cancelButton`} type={Button.Types.TEXT} onClick={confirmCancel}>
            Cancel
          </Button>
          <Button data-aid={`${dataAidPrefix}-saveButton`} onClick={handleSave}>
            {isNew ? 'Add Rule' : 'Save'}
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Rule"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
      />
    </>
  );
};

const SortOrderInput = styled(Input)`
  max-width: 65px;
  width: 65px;
`;

const Description = styled.div`
  margin-bottom: 20px;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.gray700};
`;

export default RuleEditor;
